import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useCases } from '../api/hooks';
import { SEVERITY_ORDER, STATUS_LABELS, TERMINAL_STATUSES } from '../api/types';
import type { CaseStatus, Severity } from '../api/types';
import { Card } from '../components/Card';
import { PageHeader } from '../components/Layout';
import { EmptyState, QueryBoundary } from '../components/states';
import { SeverityBadge, StatusBadge } from '../components/DomainBadges';
import { useFormatters } from '../time/format';

const STATUS_OPTIONS: (CaseStatus | 'all')[] = [
  'all',
  'open',
  'enriching',
  'awaiting_analysis',
  'awaiting_approval',
  'containing',
  'contained',
  'closed_benign',
  'closed_resolved',
  'failed',
];

const SEVERITY_OPTIONS: (Severity | 'all')[] = ['all', 'critical', 'high', 'medium', 'low', 'info'];

/**
 * Filters live in the query string, not in component state.
 *
 * So a filtered view is a link: an analyst can paste "critical cases awaiting approval" into
 * a bridge chat and the recipient sees the same list. The dashboard tiles navigate here with
 * a filter already applied for the same reason.
 */
export function Cases() {
  const [params, setParams] = useSearchParams();

  const status = (params.get('status') ?? 'all') as CaseStatus | 'all';
  const severity = (params.get('severity') ?? 'all') as Severity | 'all';
  const search = params.get('q') ?? '';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  // The server filters too, but the query is also applied here: the list is small enough to
  // hold, and filtering locally keeps the control responsive while a refetch is in flight
  // instead of blanking the table on every keystroke.
  const query = useCases({ status, severity, search });
  const { dateTime, relative } = useFormatters();

  const rows = useMemo(() => {
    const all = [...(query.data?.cases ?? [])];
    all.sort((a, b) => {
      // Open before closed, then most severe, then most recently updated. A contained case is
      // history; an open critical is the job.
      const aOpen = TERMINAL_STATUSES.has(a.status) ? 1 : 0;
      const bOpen = TERMINAL_STATUSES.has(b.status) ? 1 : 0;
      if (aOpen !== bOpen) return aOpen - bOpen;
      const bySeverity = SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity];
      if (bySeverity !== 0) return bySeverity;
      return b.updated_at.localeCompare(a.updated_at);
    });
    return all;
  }, [query.data]);

  return (
    <>
      <PageHeader
        title="Cases"
        subtitle="Correlated incidents. One case is one or more alerts about overlapping subjects."
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <label htmlFor="case-search" className="mb-1 block text-xs font-medium text-muted">
              Search
            </label>
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
                aria-hidden="true"
              />
              <input
                id="case-search"
                type="search"
                value={search}
                placeholder="Case id, title or subject"
                onChange={(event) => setParam('q', event.target.value)}
                className="w-full rounded-lg border border-line-strong bg-sunken py-1.5 pl-9 pr-3 text-sm text-body placeholder:text-subtle hover:border-brand/60"
              />
            </div>
          </div>

          <div>
            <label htmlFor="case-status" className="mb-1 block text-xs font-medium text-muted">
              Status
            </label>
            <select
              id="case-status"
              value={status}
              onChange={(event) => setParam('status', event.target.value)}
              className="rounded-lg border border-line-strong bg-sunken px-3 py-1.5 text-sm text-body hover:border-brand/60"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === 'all' ? 'All statuses' : STATUS_LABELS[option]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="case-severity" className="mb-1 block text-xs font-medium text-muted">
              Severity
            </label>
            <select
              id="case-severity"
              value={severity}
              onChange={(event) => setParam('severity', event.target.value)}
              className="rounded-lg border border-line-strong bg-sunken px-3 py-1.5 text-sm text-body hover:border-brand/60"
            >
              {SEVERITY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === 'all' ? 'All severities' : option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card className="p-0">
        <QueryBoundary
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          loadingLabel="Loading cases"
        >
          {rows.length === 0 ? (
            <EmptyState
              title="No matching cases"
              detail="Adjust the filters, or wait for the next poll cycle to bring alerts in."
            />
          ) : (
            <div className="overflow-x-auto scroll-thin">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-line text-xs uppercase tracking-wide text-subtle">
                  <tr>
                    <th scope="col" className="px-5 py-3 font-semibold">
                      Case
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Severity
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Alerts
                    </th>
                    <th scope="col" className="px-3 py-3 font-semibold">
                      Opened
                    </th>
                    <th scope="col" className="px-5 py-3 font-semibold">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((item) => (
                    <tr key={item.case_id} className="transition-colors hover:bg-raised">
                      <td className="px-5 py-3">
                        <Link to={`/cases/${encodeURIComponent(item.case_id)}`} className="block">
                          <span className="block truncate font-medium text-body">
                            {item.title}
                          </span>
                          <span className="font-mono text-[11px] text-subtle">
                            {item.case_id}
                          </span>
                        </Link>
                      </td>
                      <td className="px-3 py-3">
                        <SeverityBadge severity={item.severity} />
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-3 py-3 tabular-nums text-muted">
                        {item.alert_ids.length}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-muted">
                        {dateTime(item.opened_at)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-subtle">
                        {relative(item.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </QueryBoundary>
      </Card>
    </>
  );
}
